import { Box, Flex, Grid } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { ExchangeIcon } from 'Asset/icon';
import { Condition } from 'Component/others';
import { getMenuPermissions } from 'Helper/getMenuPermissions';
import ButtonOutline from 'UiKit/button/buttonOutline';
import ButtonSolid from 'UiKit/button/buttonSolid';
import InputComponent from 'UiKit/input';
import InputTextAreaComponent from 'UiKit/input-textarea';
import { TextBold, TextRegular } from 'UiKit/typography';

import usePatchRequestApproval from 'Domain/reschedule/usePatchRequestApproval';
import HostCard from 'View/reschedule-detail/component/change-host';
import ChangeSchedule from 'View/reschedule-detail/component/change-schedule';
import useConfirmationModal from 'View/reschedule/hooks/useConfirmationModal';
import AttachmentImage from 'View/reschedule-detail/component/attachment-image';

import styles from './styles.module.css';

const Form = ({ rescheduleDetail, isLoading }) => {
  const { t } = useTranslation('reschedule');
  const { openAcceptConfirmation, openRejectConfirmation } = useConfirmationModal();
  const { mutate: patchRequestApproval } = usePatchRequestApproval();

  const isProcessing = rescheduleDetail?.statusProgress === 'Diproses';

  const accessPermission = getMenuPermissions('schedule', 'request-approval');

  const onClickApprove = () => openAcceptConfirmation(() => patchRequestApproval({ id: rescheduleDetail?.id, statusProgress: 'Diterima' }));
  const onClickReject = () => openRejectConfirmation(() => patchRequestApproval({ id: rescheduleDetail?.id, statusProgress: 'Ditolak' }));

  return (
    <Box className={styles['form-reschedule__container']}>
      <Flex className={styles['form-reschedule__flex']}>
        <InputComponent
          className={styles['form-reschedule__input']}
          label={t('hostRequesterLabel')}
          value={rescheduleDetail?.hostRequester}
          isRequired
          isImmutable
          width="260px"
          isFetching={isLoading}
        />
        <InputComponent
          className={styles['form-reschedule__input']}
          label={t('submissionTypeLabel')}
          value={rescheduleDetail?.type?.name}
          isRequired
          isImmutable
          isFetching={isLoading}
          width="260px"
        />
      </Flex>

      <Condition statement={!rescheduleDetail?.isScheduleChange}>
        <Flex className={styles['form-reschedule__flex']}>
          <InputComponent
            className={styles['form-reschedule__input--schedule']}
            label={t('scheduleLabel')}
            value={rescheduleDetail?.schedule?.dateTime}
            isRequired
            isFetching={isLoading}
            isImmutable
          />
          <InputComponent
            className={styles['form-reschedule__input--schedule']}
            label={t('liveTitleLabel')}
            value={rescheduleDetail?.schedule?.title}
            isRequired
            isFetching={isLoading}
            isImmutable
          />
        </Flex>
      </Condition>

      <Grid className={styles['form-reschedule__grid--aligned']}>
        <Condition statement={rescheduleDetail?.isScheduleChange} fallback={<HostCard hostValue={rescheduleDetail?.hostRequester} hostLabel={t('originalHostLabel')} />}>
          <ChangeSchedule
            hostName={rescheduleDetail?.hostRequesterWithAccount}
            schedule={rescheduleDetail?.schedule}
            scheduleLabel={t('scheduleLabel')}
            titleLabel={t('liveTitleLabel')}
          />
        </Condition>

        <Box width="48px" display="flex" flexDirection="column" justifyContent="center">
          <ExchangeIcon color="var(--color-primary)" mb="8px" />
          <TextRegular textAlign="center">{rescheduleDetail?.exchangeIconLabel}</TextRegular>
        </Box>

        <Condition statement={rescheduleDetail?.isScheduleChange} fallback={<HostCard hostValue={rescheduleDetail?.targetUser} hostLabel={t('replacementHostLabel')} />}>
          <ChangeSchedule
            hostName={rescheduleDetail?.targetUserWithAccount}
            schedule={rescheduleDetail?.targetSchedule}
            scheduleLabel={t('scheduleLabel')}
            titleLabel={t('liveTitleLabel')}
          />
        </Condition>
      </Grid>

      <InputTextAreaComponent
        className={styles['form-reschedule__textarea']}
        label={t('reason')}
        value={rescheduleDetail?.reason}
        maxLength={500}
        isFetching={isLoading}
        isImmutable
      />

      <AttachmentImage className={styles['attachment-image']} images={rescheduleDetail?.images} label={t('attachmentFieldLabel')} />

      <Condition statement={isProcessing && accessPermission?.isCanEdit}>
        <Flex className={styles['footer__container']}>
          <ButtonOutline
            data-testid="reschedule-detail-action__button--reject"
            className={styles['footer__button-outline']}
            onClick={onClickReject}
            isDisabled={isLoading}
          >
            <TextBold className={styles['footer__button-outline-text']}>{t('rejectButton')}</TextBold>
          </ButtonOutline>

          <ButtonSolid
            data-testid="reschedule-detail-action__button--approve"
            className={styles['footer__button-solid']}
            onClick={onClickApprove}
            isDisabled={isLoading}
          >
            <TextBold className={styles['footer__button-solid-text']}>{t('acceptButton')}</TextBold>
          </ButtonSolid>
        </Flex>
      </Condition>
    </Box>
  );
};

export default Form;